import React, { useState, useEffect } from "react";
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  Modal,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
  CircularProgress,
  RadioGroup,
  Radio,
} from "@mui/material";
import { Plus, Minus, Folder, MapPin, Clock } from "react-feather";
import { getFinalCategoriesWithFirstAndLast } from "../../../components/getfinalcategoriesfirstandlast";

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
  },
};

export default function CategoriesVideoTab({
  categories,
  setCategories,
  availableCategories,
  locations,
  setLocations,
  availableLocations,
  partners,
  setPartners,
  availablePartners,
  setAllPartners,
  setAllLocations,
  preview,
  loading,
  frequency,
  setFrequency,
  disablePartnerEdit = false, // New prop to disable partner editing
  originalHasCategory2 = false, // New prop to indicate if video originally had category 2
}) {
  const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);
  const [isLocationModalOpen, setLocationModalOpen] = useState(false);
  const [isPartnerModalOpen, setPartnerModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [showFrequency, setShowFrequency] = useState(false);

  // Only disable category editing if the video originally had category ID 2
  const disableCategoryEdit = originalHasCategory2;
  const hasSpecialCategory = categories.some((c) => c.ID === 2);

  const toggleCategoryModal = () => {
    if (!disableCategoryEdit) {
      setCategoryModalOpen(!isCategoryModalOpen);
    }
  };
  const toggleLocationModal = () => setLocationModalOpen(!isLocationModalOpen);
  const togglePartnerModal = () => {
    if (!disablePartnerEdit) {
      setPartnerModalOpen(!isPartnerModalOpen);
    }
  };

  // Handle toggling frequency checkbox
  const handleFrequencyToggle = (e) => {
    const checked = e.target.checked;
    setShowFrequency(checked);

    // When unchecking, clear the frequency value in parent component
    if (!checked) {
      setFrequency("");
    } else {
      // Initialize with default value if empty
      setFrequency(frequency || "1");
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value.toLowerCase());

  const finalCategories =
    getFinalCategoriesWithFirstAndLast(availableCategories);

  const filteredCategories = finalCategories.filter((cat) =>
    cat.displayName.toLowerCase().includes(searchTerm)
  );

  useEffect(() => {
    setSelectedCategories(categories);
  }, [categories]);

  useEffect(() => {
    setSelectedLocations(locations);
  }, [locations]);

  useEffect(() => {
    setSelectedPartners(partners);
  }, [partners]);

  useEffect(() => {
    // Initialize the frequency field visibility based on selected categories
    const hasFrequencyCategories = categories.some(
      (c) => c.ID === 2 || c.ID === 6 || c.ID === 20
    );

    if (hasFrequencyCategories) {
      // If frequency is already set, show the frequency field
      if (frequency) {
        setShowFrequency(true);
      }
    } else {
      // If no relevant categories are selected, clear frequency and hide field
      setShowFrequency(false);
      setFrequency("");
    }
  }, [categories, frequency]);

  const filteredLocations = availableLocations.filter(
    (loc) =>
      loc.name.toLowerCase().includes(searchTerm) &&
      selectedPartners.some((partner) => partner.ID === loc.partner_id)
  );

  const handleSelection = (item, selectedItems, setSelectedItems) => {
    if (selectedItems.some((i) => i.ID === item.ID)) {
      setSelectedItems(selectedItems.filter((i) => i.ID !== item.ID));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleCategorySelection = (item) => {
    setSelectedCategories([item]); // single selection enforcement
  };

  const toggleSelectAll = (availableItems, selectedItems, setSelectedItems) => {
    if (selectedItems.length === availableItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems([...availableItems]);
    }
  };

  const saveCategories = () => {
    setCategories(selectedCategories);
    toggleCategoryModal();
  };

  const saveLocations = () => {
    setLocations(selectedLocations);
    toggleLocationModal();

    checkIfAllLocations(selectedLocations, selectedPartners);
  };

  const savePartners = () => {
    if (disablePartnerEdit) return; // Prevent saving when editing is disabled

    setPartners(selectedPartners);
    togglePartnerModal();

    //remove the locations that are not associated with the selected partners
    const updatedLocations = availableLocations.filter((loc) =>
      selectedPartners.some((partner) => partner.ID === loc.partner_id)
    );

    if (selectedPartners.length === availablePartners.length) {
      setLocations(updatedLocations);
      setSelectedLocations(updatedLocations);
    }

    checkIfAllLocations(updatedLocations, selectedPartners);
  };

  const checkIfAllLocations = (sentLocations, partners) => {
    const updatedLocations = availableLocations.filter((loc) =>
      partners.some((partner) => partner.ID === loc.partner_id)
    );

    if (sentLocations.length === updatedLocations.length) {
      setAllLocations(true);
    } else {
      setAllLocations(false);
    }
  };

  const handleRemoveCategory = (ID) => {
    if (disableCategoryEdit) return; // Prevent removal when editing is disabled
    
    const updatedCategories = selectedCategories.filter((c) => c.ID !== ID);
    setCategories(updatedCategories);
    setSelectedCategories(updatedCategories);
    setSelectedPartners([]);
    setSelectedLocations([]);
    setLocations([]);
    setPartners([]);
  };

  const handleRemoveLocation = (ID) => {
    const updatedLocations = selectedLocations.filter((l) => l.ID !== ID);
    setLocations(updatedLocations);
    setSelectedLocations(updatedLocations);

    checkIfAllLocations(updatedLocations, selectedPartners);
  };

  const handleRemovePartner = (ID) => {
    if (disablePartnerEdit) return; // Prevent removal when editing is disabled

    const updatedPartners = selectedPartners.filter((p) => p.ID !== ID);
    setPartners(updatedPartners);
    setSelectedPartners(updatedPartners);
    const updatedLocations = availableLocations.filter(
      (loc) => loc.partner_id !== ID
    );
    setLocations([]);
    setSelectedLocations([]);

    checkIfAllLocations(updatedLocations, updatedPartners);
  };

  return (
    <>
      <h1 className="add-video-title">Selecteaza categorii si locatii</h1>
      <Divider style={{ marginBottom: "20px" }} />

      {/* Categories Section */}
      <div style={{ display: "flex", columnGap: "7px", alignItems: "center" }}>
        <Folder fontSize={"1.25rem"} />
        <span 
          style={{ 
            fontSize: "1.25rem",
            opacity: disableCategoryEdit ? 0.6 : 1 
          }}
        >
          Categorii {disableCategoryEdit && "(nu poate fi modificat)"}
        </span>
        <IconButton 
          onClick={toggleCategoryModal} 
          disabled={preview || disableCategoryEdit}
        >
          <Plus />
        </IconButton>
      </div>
      {loading ? (
        <CircularProgress style={{ alignSelf: "center" }} />
      ) : (
        <List>
          {categories.map((category) => (
            <ListItem
              key={category.ID}
              style={{ display: "flex", alignItems: "center" }}
            >
              <ListItemText primary={category.displayName} />{" "}
              <IconButton
                onClick={() => handleRemoveCategory(category.ID)}
                disabled={preview || disableCategoryEdit}
              >
                <Minus />
              </IconButton>
            </ListItem>
          ))}
        </List>
      )}

      {/* Frequency Field - only visible when category 2 or 3 is selected */}

      {/* Partners Section */}
      {categories.some((c) => c.ID === 2) && (
        <>
          <div
            style={{ display: "flex", columnGap: "7px", alignItems: "center" }}
          >
            <Folder fontSize={"1.25rem"} />
            <span
              style={{
                fontSize: "1.25rem",
                opacity: disablePartnerEdit ? 0.6 : 1,
              }}
            >
              Partener {disablePartnerEdit && "(nu poate fi modificat)"}
            </span>
            <IconButton
              onClick={togglePartnerModal}
              disabled={preview || disablePartnerEdit}
            >
              <Plus />
            </IconButton>
          </div>
          <List>
            {partners.map((partner) => (
              <ListItem
                key={partner.ID}
                style={{ display: "flex", alignItems: "center" }}
              >
                <ListItemText primary={partner.name} />
                <IconButton
                  onClick={() => handleRemovePartner(partner.ID)}
                  disabled={preview || disablePartnerEdit}
                >
                  <Minus />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Locations Section */}
      {partners.length > 0 &&
        partners.length !== availablePartners.length &&
        categories.some((c) => c.ID === 2) && (
          <>
            <div
              style={{
                display: "flex",
                columnGap: "7px",
                alignItems: "center",
              }}
            >
              <MapPin fontSize={"1.25rem"} />
              <span style={{ fontSize: "1.25rem" }}>Locatii</span>
              <IconButton onClick={toggleLocationModal} disabled={preview}>
                <Plus />
              </IconButton>
            </div>
            <List>
              {locations.map((location) => (
                <ListItem
                  key={location.ID}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <ListItemText primary={location.name} />
                  <IconButton
                    onClick={() => handleRemoveLocation(location.ID)}
                    disabled={preview}
                  >
                    <Minus />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </>
        )}

      {categories.some((c) => c.ID === 2 || c.ID === 6 || c.ID === 20) && (
        <div style={{ marginBottom: "20px" }}>
          <FormControlLabel
            control={
              <Checkbox
                color="black"
                checked={showFrequency}
                onChange={handleFrequencyToggle}
                disabled={preview}
              />
            }
            label={
              <div
                style={{ display: "flex", alignItems: "center", gap: "7px" }}
              >
                <Clock fontSize={"1.25rem"} />
                <span style={{ fontSize: "1.25rem" }}>Permite frecventa</span>
              </div>
            }
          />

          {showFrequency && (
            <TextField
              fullWidth
              placeholder="Frecventa"
              value={frequency}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty field temporarily during editing, but validate when blurring
                if (
                  value === "" ||
                  (/^[0-9]+$/.test(value) && parseInt(value) > 0)
                ) {
                  setFrequency(value);
                }
              }}
              onBlur={() => {
                // When user leaves the field, make sure a value is set
                if (frequency === "" || parseInt(frequency) <= 0) {
                  setFrequency("1");
                }
              }}
              onFocus={(e) => {
                // Select all text when focusing for easier editing
                e.target.select();
              }}
              type="number"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: "1",
              }}
              disabled={preview}
              style={{ marginTop: "10px" }}
              sx={styles.select}
            />
          )}
        </div>
      )}

      {/* Category Modal */}
      <Modal open={isCategoryModalOpen && !disableCategoryEdit} onClose={toggleCategoryModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <h2 style={{ fontFamily: "SeagoeUIBold" }}>Selecteaza o categorie</h2>
          <TextField
            fullWidth
            placeholder="Cauta categorii"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ marginBottom: "16px" }}
          />
          <Box
            sx={{
              maxHeight: "50vh",
              overflowY: "scroll",
              marginBottom: "16px",
              border: "1px solid #ddd",
            }}
          >
            <RadioGroup
              value={selectedCategories[0]?.ID.toString()}
              onChange={(e) => {
                const selected = filteredCategories.find(
                  (cat) => cat.ID.toString() === e.target.value
                );
                if (selected) {
                  handleCategorySelection(selected); // not handleSelection
                }
              }}
            >
              {filteredCategories.map((category) => (
                <ListItem key={category.ID}>
                  <FormControlLabel
                    value={category.ID.toString()}
                    control={<Radio color="black" />}
                    label={category.displayName}
                  />
                </ListItem>
              ))}
            </RadioGroup>
          </Box>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <button className="add-video-button" onClick={toggleCategoryModal}>
              Renunta
            </button>
            <button className="add-video-button" onClick={saveCategories}>
              Salveaza
            </button>
          </Box>
        </Box>
      </Modal>

      {/* Partners Modal */}
      <Modal
        open={isPartnerModalOpen && !disablePartnerEdit}
        onClose={togglePartnerModal}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            // maxHeight: "80vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <h2 style={{ fontFamily: "SeagoeUIBold" }}>Select Partners</h2>
          <TextField
            sx={styles.select}
            fullWidth
            placeholder="Search partners"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ marginBottom: "16px" }}
          />
          <Box
            sx={{
              maxHeight: "50vh",
              overflowY: "scroll",
              marginBottom: "16px",
              border: "1px solid #ddd",
            }}
          >
            <List>
              {availablePartners.map((partner) => (
                <ListItem key={partner.ID}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedPartners.some(
                          (c) => c.ID === partner.ID
                        )}
                        onChange={() =>
                          handleSelection(
                            partner,
                            selectedPartners,
                            setSelectedPartners
                          )
                        }
                      />
                    }
                    label={partner.name}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
          {selectedPartners.length > 1 && (
            <div
              style={{ color: "#d32f2f", marginTop: "8px", fontSize: "14px" }}
            >
              Nu puteti selecta mai mult de un partener
            </div>
          )}
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <button className="add-video-button" onClick={togglePartnerModal}>
              Cancel
            </button>
            <button
              className="add-video-button"
              onClick={savePartners}
              disabled={selectedPartners.length > 1}
              style={{
                backgroundColor: selectedPartners.length > 1 ? "#ccc" : "#000",
                pointerEvents: selectedPartners.length > 1 ? "none" : "auto",
              }}
            >
              Save
            </button>
          </Box>
        </Box>
      </Modal>

      {/* Location Modal */}
      <Modal open={isLocationModalOpen} onClose={toggleLocationModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            // maxHeight: "80vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <h2 style={{ fontFamily: "SeagoeUIBold" }}>Select Locations</h2>
          <TextField
            sx={styles.select}
            fullWidth
            placeholder="Search locations"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ marginBottom: "16px" }}
          />
          <button
            className="add-video-button"
            onClick={() =>
              toggleSelectAll(
                filteredLocations,
                selectedLocations,
                setSelectedLocations
              )
            }
            style={{ marginBottom: "16px", width: "100%" }}
          >
            {selectedLocations.length === filteredLocations.length
              ? "Deselecteaza toate"
              : "Selecteaza toate"}
          </button>
          <Box
            sx={{
              maxHeight: "50vh",
              overflowY: "scroll",
              marginBottom: "16px",
              border: "1px solid #ddd",
            }}
          >
            <List>
              {filteredLocations.map((location) => (
                <ListItem key={location.ID}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedLocations.some(
                          (l) => l.ID === location.ID
                        )}
                        onChange={() =>
                          handleSelection(
                            location,
                            selectedLocations,
                            setSelectedLocations
                          )
                        }
                      />
                    }
                    label={location.name}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <button className="add-video-button" onClick={toggleLocationModal}>
              Renunta
            </button>
            <button className="add-video-button" onClick={saveLocations}>
              Salveaza
            </button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
